// A real .xlsx, written by hand, with no dependency.
//
// ── Why not CSV, and why not SheetJS ───────────────────────────────────────
//
// CSV is the obvious answer and it is wrong here for three reasons, all of
// which corrupt data silently rather than failing:
//
//   • **Excel retypes every cell it reads.** A unique number is a STRING that
//     happens to be digits. `007712` opens as 7712, and a long one flips to
//     `8.84811E+11`. The sheet then no longer matches the device, and nothing
//     says so.
//   • **The delimiter is a locale setting, not a standard.** Excel splits on
//     the system list separator, so a comma file opens as a single column on
//     any machine set to a semicolon locale.
//   • **A leading `=`, `+`, `-` or `@` is a FORMULA.** Device names are typed
//     by customers; a CSV is an execution vector into whoever opens it.
//
// SheetJS solves all three and costs ~400 KB in a bundle that is already over
// the recommended size AND is shipped over the air to phones — for a sheet only
// a Super Admin ever exports.
//
// An .xlsx is a ZIP of XML. Written with the cells typed as `inlineStr` all
// three problems disappear at the source: Excel is TOLD the cell is text, so it
// never retypes it, there is no delimiter to guess, and a formula has to be
// declared with `<f>` — a string that starts with `=` is just a string. The ZIP
// is written STORE-only (no compression), which needs no deflate implementation
// and produces a file Excel opens exactly the same way.
//
// ⚠️ ELEMENT ORDER IN SpreadsheetML IS SCHEMA-ENFORCED. `dimension`,
// `sheetViews`, `sheetFormatPr`, `cols`, `sheetData`, then `autoFilter` — get
// them out of order and Excel does not report a parse error, it offers to
// "repair" the file and drops whatever it did not understand.
//
// ── Proven against Excel, not only against the tests ───────────────────────
// The suite reads the archive back through its own central directory, which
// catches a malformed ZIP or XML but cannot say what EXCEL does with it. A
// generated workbook was opened in Excel 16.0 and read back over COM:
//
//   • both sheets present, autofilter, frozen pane and the bold header all
//     intact — those are the first things a repair strips, so their survival
//     is what says no repair happened
//   • `007712` and `884811000000` came back as String, keeping the leading
//     zeros and the digits — the CSV failure this file exists to avoid
//   • Sr. No. came back as a number
//   • a device named `=1+1 & <b>` came back with HasFormula False and its text
//     unchanged
//
// Re-run that check after any change here: the tests cannot see a repair.

// ── UTF-8, encoded here rather than with TextEncoder ───────────────────────
//
// ⚠️ jsdom 16 — the one CRA 5 runs tests on — has no global TextEncoder, so a
// writer built on it cannot be tested at all. Fifteen lines is a smaller price
// than an untested binary format.
export function utf8(str) {
  const s = String(str ?? '');
  const out = [];
  for (let i = 0; i < s.length; i += 1) {
    let cp = s.charCodeAt(i);
    // A code point past the BMP arrives as a surrogate PAIR; encoding the two
    // halves separately produces the mojibake Excel shows as two boxes.
    if (cp >= 0xD800 && cp <= 0xDBFF && i + 1 < s.length) {
      const lo = s.charCodeAt(i + 1);
      if (lo >= 0xDC00 && lo <= 0xDFFF) {
        cp = 0x10000 + ((cp - 0xD800) << 10) + (lo - 0xDC00);
        i += 1;
      }
    }
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xC0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) {
      out.push(0xE0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    } else {
      out.push(
        0xF0 | (cp >> 18), 0x80 | ((cp >> 12) & 63),
        0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63),
      );
    }
  }
  return Uint8Array.from(out);
}

// ── CRC-32, which the ZIP central directory requires ───────────────────────

let CRC_TABLE = null;

const crcTable = () => {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Int32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    t[n] = c;
  }
  CRC_TABLE = t;
  return t;
};

export function crc32(bytes) {
  const t = crcTable();
  let c = -1;
  for (let i = 0; i < bytes.length; i += 1) {
    c = t[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  }
  return (c ^ -1) >>> 0;
}

// ── ZIP (store only) ───────────────────────────────────────────────────────

const u16 = (n) => [n & 0xFF, (n >>> 8) & 0xFF];
const u32 = (n) => [n & 0xFF, (n >>> 8) & 0xFF, (n >>> 16) & 0xFF, (n >>> 24) & 0xFF];

// 1980-01-01 00:00, the earliest a DOS timestamp can express — and FIXED, not
// the clock. The entry date is not visible anywhere in Excel, while a real
// timestamp would make every generated workbook a different byte string and the
// tests below unable to say anything about the bytes.
const DOS_DATE = 0x0021;
const DOS_TIME = 0x0000;

/**
 * Pack named byte blobs into a ZIP archive.
 *
 * `files` is [{ name, data: Uint8Array }] — names use forward slashes and are
 * ASCII, which is all the OPC part names ever are.
 */
export function zipStore(files) {
  const local = [];
  const central = [];
  let offset = 0;

  files.forEach((f) => {
    const name = utf8(f.name);
    const crc = crc32(f.data);
    const size = f.data.length;

    const header = [
      ...u32(0x04034B50), ...u16(20), ...u16(0), ...u16(0),
      ...u16(DOS_TIME), ...u16(DOS_DATE),
      ...u32(crc), ...u32(size), ...u32(size),
      ...u16(name.length), ...u16(0),
    ];
    local.push(Uint8Array.from(header), name, f.data);

    central.push(Uint8Array.from([
      ...u32(0x02014B50), ...u16(20), ...u16(20), ...u16(0), ...u16(0),
      ...u16(DOS_TIME), ...u16(DOS_DATE),
      ...u32(crc), ...u32(size), ...u32(size),
      ...u16(name.length), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0), ...u32(0),
      ...u32(offset),
    ]), name);

    offset += header.length + name.length + size;
  });

  const centralSize = central.reduce((n, b) => n + b.length, 0);
  const end = Uint8Array.from([
    ...u32(0x06054B50), ...u16(0), ...u16(0),
    ...u16(files.length), ...u16(files.length),
    ...u32(centralSize), ...u32(offset), ...u16(0),
  ]);

  const parts = [...local, ...central, end];
  const total = parts.reduce((n, b) => n + b.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  parts.forEach((b) => { out.set(b, at); at += b.length; });
  return out;
}

// ── SpreadsheetML ──────────────────────────────────────────────────────────

/**
 * ⚠️ A CONTROL CHARACTER IS ILLEGAL IN XML 1.0 AND EXCEL REJECTS THE WHOLE
 * WORKBOOK, not the cell that carries it. Device names come from a text field on
 * a phone; one stray character in one name would make the entire export
 * unopenable, so they are dropped rather than escaped.
 */
const xml = (v) => String(v ?? '')
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** 0-based column index -> A, B, … Z, AA, AB … */
export function colLetter(index) {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

const textCell = (ref, value, style) => `<c r="${ref}"${style ? ` s="${style}"` : ''}`
  + ` t="inlineStr"><is><t xml:space="preserve">${xml(value)}</t></is></c>`;

/*
 * A cell, typed.
 *
 * ⚠️ THE DEFAULT IS TEXT, AND THAT IS THE WHOLE POINT OF THIS FILE. Only a
 * column that ASKS to be a number gets `<v>`; everything else is an inline
 * string, so a unique number keeps its leading zeros, its length and its
 * identity. An empty value is written as no cell at all rather than an empty
 * string — Excel treats a blank cell and an empty string differently in
 * COUNTA and in a filter, and "no device name recorded" is a blank.
 */
const cell = (ref, value, col) => {
  if (value == null || value === '') return '';
  if (col.type === 'number') {
    const n = Number(value);
    if (Number.isFinite(n)) return `<c r="${ref}"><v>${n}</v></c>`;
  }
  return textCell(ref, value, 0);
};

function sheetXml(sheet) {
  const cols = sheet.columns || [];
  const rows = sheet.rows || [];
  if (!cols.length) {
    return `${DECL}<worksheet xmlns="${NS}"><sheetData/></worksheet>`;
  }

  const lastCol = colLetter(cols.length - 1);
  const dim = `A1:${lastCol}${rows.length + 1}`;

  const colsXml = `<cols>${cols.map((c, i) => `<col min="${i + 1}" max="${i + 1}"`
    + ` width="${c.width || 16}" customWidth="1"/>`).join('')}</cols>`;

  // Frozen, because a device list runs past one screen and a scrolled sheet
  // with no column headers is four anonymous columns — the same reason the
  // printed sheet repeats its thead.
  const views = sheet.freezeHeader === false
    ? `<sheetViews><sheetView workbookViewId="0"/></sheetViews>`
    : `<sheetViews><sheetView workbookViewId="0"><pane ySplit="1"`
      + ` topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`;

  const head = `<row r="1">${cols
    .map((c, i) => textCell(`${colLetter(i)}1`, c.label, 1)).join('')}</row>`;

  const body = rows.map((r, ri) => {
    const n = ri + 2;
    const cells = cols.map((c, ci) => cell(`${colLetter(ci)}${n}`, r[c.key], c)).join('');
    return `<row r="${n}">${cells}</row>`;
  }).join('');

  // ⚠️ AFTER sheetData, never before — see the note at the top of this file.
  const filter = sheet.autoFilter === false ? '' : `<autoFilter ref="${dim}"/>`;

  return `${DECL}<worksheet xmlns="${NS}"><dimension ref="${dim}"/>${views}`
    + `<sheetFormatPr defaultRowHeight="15"/>${colsXml}`
    + `<sheetData>${head}${body}</sheetData>${filter}</worksheet>`;
}

/*
 * ⚠️ THE FILLS ARE NOT OPTIONAL AND NOR IS THEIR ORDER. Excel requires fill 0
 * to be `none` and fill 1 to be `gray125`; a styles part without both is the
 * single most common reason a hand-written workbook opens as "we found a
 * problem with some content".
 */
const STYLES = `${DECL}<styleSheet xmlns="${NS}">`
  + `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>`
  + `<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>`
  + `<fills count="2"><fill><patternFill patternType="none"/></fill>`
  + `<fill><patternFill patternType="gray125"/></fill></fills>`
  + `<borders count="1"><border/></borders>`
  + `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>`
  + `<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`
  + `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>`
  + `</styleSheet>`;

/**
 * A sheet name Excel will accept.
 *
 * ⚠️ Excel does not truncate or substitute — it refuses the workbook. 31
 * characters, and none of : \ / ? * [ ].
 */
export const sheetName = (name, fallback = 'Sheet') => (
  String(name ?? '').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || fallback
);

/**
 * Build a workbook.
 *
 * `sheets` is [{ name, columns: [{ key, label, width, type }], rows: [{}] }].
 * `type: 'number'` opts a column into numeric cells; everything else is text.
 *
 * -> Uint8Array of the .xlsx file.
 */
export function buildWorkbook(sheets) {
  const list = (sheets || []).length ? sheets : [{ name: 'Sheet1', columns: [], rows: [] }];

  const contentTypes = `${DECL}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
    + `<Default Extension="xml" ContentType="application/xml"/>`
    + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
    + list.map((s, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml"`
      + ` ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
    + `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>`
    + `</Types>`;

  const rootRels = `${DECL}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + `<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

  const workbook = `${DECL}<workbook xmlns="${NS}" xmlns:r="${NS_REL}"><sheets>`
    + list.map((s, i) => `<sheet name="${xml(sheetName(s.name, `Sheet${i + 1}`))}"`
      + ` sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
    + `</sheets></workbook>`;

  const workbookRels = `${DECL}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + list.map((s, i) => `<Relationship Id="rId${i + 1}" Type="${NS_REL}/worksheet"`
      + ` Target="worksheets/sheet${i + 1}.xml"/>`).join('')
    + `<Relationship Id="rId${list.length + 1}" Type="${NS_REL}/styles" Target="styles.xml"/>`
    + `</Relationships>`;

  const files = [
    { name: '[Content_Types].xml', data: utf8(contentTypes) },
    { name: '_rels/.rels', data: utf8(rootRels) },
    { name: 'xl/workbook.xml', data: utf8(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: utf8(workbookRels) },
    { name: 'xl/styles.xml', data: utf8(STYLES) },
    ...list.map((s, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: utf8(sheetXml(s)),
    })),
  ];

  return zipStore(files);
}

export default buildWorkbook;
